import { Expense, type TripFolder } from "@finway-group/shared/lib/models"
import { ExpenseDocumentTypeEnum } from "@finway-group/shared/lib/models/expense/expenseDocumentType.enum"
import moment from "moment"
import { useEffect, useState } from "react"
import { fetchVendor } from "~Shared/queries/vendors/vendor.queries"
import { ApprovalProcessService, EmployeeService, OcrService, UserService } from "~Shared/services"
import { NotificationDurationSec, NotificationService, NotificationTypeEnum } from "~Shared/services/notification.service"
import { store } from "~Shared/store"
import { updateExpense as dispatchUpdateExpense } from "~Shared/store/actions/expense/expenseActions"
import { checkIsValidationError, extractErrors, getExpenseValidationErrorTip } from "~Shared/utils/errorHandler.utils"
import {
    applyExpenseAccountingRulesFromSettingsIfVendorChanged,
    deleteUnnecessaryExpenseFormData,
    flipCreditNoteAmountSigns,
    isHospitalityExpense,
    syncExpenseSplits,
} from "~Shared/utils/expense.utils"
import { hasPerformanceDateOutOfHardLimit, initializeOcrSettings, resolveOcrSettingsForExpense, shouldApplyOcrCreditNote } from "~Shared/utils/ocr.utils"
import { type FillFormWithOcrDataParams, type OcrSettingsWithSetters, type OnUpdateExpenseFromOcrParams, type UpdatedOcrObject } from "~Shared/utils/ocr.utils.types"

/**
 * Holds the OCR toggles of the expense that is currently on screen. The expense detail page is reused when the user
 * navigates from one expense to the next, so the toggles have to be resolved again for every expense instead of
 * keeping whatever the previously opened expense left behind.
 */
export const useExpenseOcrSettings = (expense: Expense): OcrSettingsWithSetters => {
    const defaultSettings = initializeOcrSettings(UserService.getLoggedInEmployeeProfile())
    const settingsForExpense = resolveOcrSettingsForExpense({ expense, defaultSettings })
    const [isOcrEnabled, setIsOcrEnabled] = useState(settingsForExpense.isOcrEnabled)
    const [isOcrItemizationEnabled, setIsOcrItemizationEnabled] = useState(settingsForExpense.isOcrItemizationEnabled)

    // The expense id is a dependency so that switching to another expense restores the defaults even when both
    // expenses resolve to the same toggles and the user changed them in between.
    useEffect(() => {
        setIsOcrEnabled(settingsForExpense.isOcrEnabled)
        setIsOcrItemizationEnabled(settingsForExpense.isOcrItemizationEnabled)
    }, [expense.id, settingsForExpense.isOcrEnabled, settingsForExpense.isOcrItemizationEnabled])

    return {
        isOcrEnabled,
        isOcrItemizationEnabled,
        isOcrItemizationDisplayed: settingsForExpense.isOcrItemizationDisplayed,
        setIsOcrEnabled,
        setIsOcrItemizationEnabled,
    }
}

// TODO: these hooks should be refactored to act as real hooks, e.g. using useDispatch instead of passing dispatch into the function
export const onUpdateExpenseFromOcr = async ({ updatedOcrObject, activeExpense, taxes, t, isOcrItemizationEnabled, company, expense, dispatch }: OnUpdateExpenseFromOcrParams) => {
    try {
        const users = await EmployeeService.fetchAllEmployees()
        const activeExpenseAsPrinted = flipCreditNoteAmountSigns(activeExpense)
        const updateData = await OcrService.buildOcrUpdateData({
            ocrObject: updatedOcrObject,
            existingData: activeExpenseAsPrinted,
            taxes,
            t,
            ocrItemizationEnabled: isOcrItemizationEnabled,
            isHospitality: isHospitalityExpense(activeExpense),
        })
        const sanitizedUpdateData = deleteUnnecessaryExpenseFormData(updateData)

        if (
            hasPerformanceDateOutOfHardLimit({
                kind: activeExpense.kind,
                datePurchased: sanitizedUpdateData.datePurchased,
                performancePeriod: sanitizedUpdateData.performancePeriod,
            })
        ) {
            NotificationService.send(
                NotificationTypeEnum.ERROR,
                t("error:error"),
                `${t("validation:out_of_range.date")} ${t("error:request.missing_field.must_be_adjusted")}`,
                NotificationDurationSec.ERROR_MESSAGE,
            )
            return
        }

        let updatedExpense = syncExpenseSplits(
            new Expense(
                flipCreditNoteAmountSigns({
                    ...activeExpenseAsPrinted,
                    ...sanitizedUpdateData,
                    documentType: updatedOcrObject.documentType ?? activeExpense.documentType,
                    skipEmail: true,
                    isOcrUpdate: true,
                }),
            ),
            sanitizedUpdateData.splitType ?? activeExpense.splitType,
        )

        const hasVendorChanged = activeExpense.vendor?._id !== updatedExpense.vendor?._id
        const vendorForAccountingRules = hasVendorChanged ? await fetchVendor(updatedExpense.vendor?._id) : undefined
        updatedExpense = applyExpenseAccountingRulesFromSettingsIfVendorChanged(updatedExpense, activeExpense, company, users, vendorForAccountingRules)

        const vendorForPaymentTerm = hasVendorChanged ? vendorForAccountingRules : await fetchVendor(activeExpense.vendor?._id)
        const paymentTerm = vendorForPaymentTerm?.rule?.paymentTerm
        const invoiceDateForDueDate = hasVendorChanged ? updatedExpense.invoiceDate : sanitizedUpdateData.invoiceDate
        if (!sanitizedUpdateData.invoiceDueDate && paymentTerm && invoiceDateForDueDate) {
            updatedExpense = new Expense({
                ...updatedExpense,
                invoiceDueDate: moment(invoiceDateForDueDate).add(paymentTerm, "days"),
            })
        }

        // Rebuild approval process with ocr changes. Expenses inside folder do not change its aproval processes and follow the folder approval processes.
        if (!updatedExpense.folderId) {
            const approvalProcesses = await ApprovalProcessService.getApprovalProcessesAfterExpenseUpdate(updatedExpense, company, {
                previousExpense: expense,
                awaitConfirmation: true,
                ocrConfirmation: true,
            })
            if (!approvalProcesses) {
                return
            }
            updatedExpense.approvalProcesses = approvalProcesses
        } else {
            const folder = expense as TripFolder
            const isFolderAmountChangeApproved = await ApprovalProcessService.confirmFolderAmountChange(
                updatedExpense.totalGrossPrice - activeExpense.totalGrossPrice,
                folder,
                company,
            )
            if (!isFolderAmountChangeApproved) {
                return
            }
        }

        await dispatchUpdateExpense(updatedExpense.id, updatedExpense)(dispatch)
        NotificationService.send(NotificationTypeEnum.INFO, t("notification:ocr.processed.title"), t("notification:ocr.processed_and_updated.message"), 60)
    } catch (err) {
        if (checkIsValidationError(err)) {
            const { response } = err
            const [firstError] = response.data.errors
            const tip = getExpenseValidationErrorTip(firstError.path as keyof Expense, t)
            NotificationService.send(NotificationTypeEnum.ERROR, t("error:error"), `${extractErrors(response)[0]} ${tip}`, NotificationDurationSec.ERROR_MESSAGE, undefined)
            return
        }
        NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:ocr.title"))
    }
}

export const fillFormWithOcrData = async ({
    formInstance,
    expense,
    ocrObject,
    taxes,
    updateExpense: updateExpenseFn,
    setPrefilledVendorData,
    t,
    ocrItemizationEnabled,
    getCurrentFormState,
}: FillFormWithOcrDataParams) => {
    const isHospitality = isHospitalityExpense(expense)
    const requester = EmployeeService.getEmployeeById(expense.requestedBy._id)
    const formFields = formInstance.getFieldsValue()
    if (!formFields.paymentOption) {
        formFields.paymentOption = expense.paymentOption
    }
    if (!formFields.kind) {
        formFields.kind = expense.kind
    }

    const existingData = {
        ...formFields,
        costCenter: formFields.costCenter ?? expense.costCenter,
        costCenter2: formFields.costCenter2 ?? expense.costCenter2,
        splits: formFields.splits?.length ? formFields.splits : expense.splits,
        isReimbursement: expense.isReimbursement,
        splitType: expense.splitType,
        approvalProcesses: expense.approvalProcesses,
        isAutoGenerated: expense.isAutoGenerated,
        paymentOption: expense.paymentOption,
    }

    const updateData: UpdatedOcrObject = await OcrService.buildOcrUpdateData({
        ocrObject,
        existingData,
        taxes,
        t,
        ocrItemizationEnabled,
        isHospitality,
        requester,
    })

    if (ocrObject.vendor && !updateData.vendor) {
        setPrefilledVendorData({ ...ocrObject.vendor, description: t("info:filled_by_ocr") })
    }

    const { expense: currentExpense, isDocumentTypeSetByUser } = getCurrentFormState()
    const isCreditNoteApplied = shouldApplyOcrCreditNote({
        ocrObject,
        expense: currentExpense,
        isNettingEnabled: Boolean(store.getState().company.item?.vendorPaymentNettingEnabled),
        isDocumentTypeSetByUser,
    })
    updateExpenseFn({ ...updateData, documentType: isCreditNoteApplied ? ExpenseDocumentTypeEnum.CREDIT_NOTE : currentExpense.documentType })
}
